import { type AvailableService, serviceProviderScope } from '@/api/services';
import { categoryOfScope, CANONICAL_CDN_SCOPE_PRIORITY } from '@/lib/service-categories';
import { isWafCapable } from '@vnetwork/margarita-shell/waf-service';

/**
 * Interim client-side consolidation of CDN provider services.
 *
 * A customer can hold several CDN service rows (one per provider backend —
 * vEdge/Raven, Manticore/Eagle, EdgeNext/Hawk, Bunny, …) for the same domain
 * set. The customer-facing model is ONE "Multi-CDN Orchestration" service, so
 * we group those rows by organization and surface a single canonical card.
 *
 * This mirrors the eventual `parent_service_id` grouping the backend will own;
 * until then we derive the umbrella purely from `useMyServices()` data.
 */
export interface CDNServiceGroup {
  /** The service whose id/route the umbrella card opens. */
  canonical: AvailableService;
  /** All CDN provider services in the org-group (incl. canonical). */
  members: AvailableService[];
  /**
   * Number of distinct CDN vendor backends (vEdge, EdgeNext, Bunny, …) behind
   * the umbrella — counted by provider scope, NOT by service row. An org can
   * hold many service rows of the same vendor (multi-account tenancies), so the
   * row count would overstate "providers".
   */
  providerCount: number;
}

function scopePriority(scope: string): number {
  const i = (CANONICAL_CDN_SCOPE_PRIORITY as readonly string[]).indexOf(scope);
  return i === -1 ? CANONICAL_CDN_SCOPE_PRIORITY.length : i;
}

/** Pick the customer-facing canonical service for a group of CDN provider services. */
export function pickCanonicalCDN(group: AvailableService[]): AvailableService {
  return [...group].sort((a, b) => {
    const sa = serviceProviderScope(a);
    const sb = serviceProviderScope(b);
    const pa = scopePriority(sa);
    const pb = scopePriority(sb);
    if (pa !== pb) return pa - pb;
    return sa.localeCompare(sb);
  })[0];
}

/** Group CDN-typed services by organization into one umbrella per org. */
export function groupCDNServices(services: AvailableService[]): CDNServiceGroup[] {
  const byOrg = new Map<string, AvailableService[]>();
  for (const s of services) {
    if (categoryOfScope(serviceProviderScope(s)) !== 'cdn') continue;
    // Owl/Mlytics rides the CDN scope catalog-wise but is surfaced as a WAF
    // service under Security — keep it out of the Multi-CDN umbrella.
    if (isWafCapable(s)) continue;
    const key = s.organization_id || s.id;
    const arr = byOrg.get(key) ?? [];
    arr.push(s);
    byOrg.set(key, arr);
  }
  const groups: CDNServiceGroup[] = [];
  for (const members of byOrg.values()) {
    const distinctScopes = new Set(members.map(serviceProviderScope));
    groups.push({
      canonical: pickCanonicalCDN(members),
      members,
      providerCount: distinctScopes.size,
    });
  }
  return groups;
}
